// filepath: /var/www/HRMS/frontend-server/src/pages/employee/ReferenceManagement.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import Select from 'react-select';
import { FiEdit2, FiSearch, FiPlus, FiX } from 'react-icons/fi';

export default function ReferenceManagement() {
  const { user } = useContext(AuthContext);

  const [references, setReferences] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Pagination and search
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalReferences, setTotalReferences] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Station dropdown
  const [stations, setStations] = useState([]);

  // Classification and Type options
  const classificationOptions = [
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'D', label: 'D' },
    { value: 'E', label: 'E' }
  ];

  const typeOptions = [
    { value: 'Interior', label: 'Interior' },
    { value: 'Designer', label: 'Designer' },
    { value: 'Architect', label: 'Architect' }
  ];

  // Form fields
  const [form, setForm] = useState({
    name: '',
    phoneNumber: '',
    classification: 'A',
    type: 'Interior',
    station: '',
    createdBy: user?.user?._id
  });

  useEffect(() => {
    fetchReferences();
  }, [page, limit, searchTerm]);

  useEffect(() => {
    fetchStations();
  }, []);

  // Dark mode sync with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setDarkMode(savedMode === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Fetch available stations for dropdown
  const fetchStations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/stations`);
      const stationOptions = response.data.map((st) => ({
        value: st._id,
        label: st.name
      }));
      setStations(stationOptions);
    } catch (err) {
      Swal.fire('Error', 'Failed to fetch stations', 'error');
    }
  };

  // Fetch References with pagination + search
  const fetchReferences = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/references`, {
        params: {
          limit,
          page,
          search: searchTerm,
          userCompanyId: user.company
        }
      });
      setReferences(response.data.references);
      setTotalReferences(response.data.total);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch references', 'error');
    }
  };

  // Validate form
  const validateForm = () => {
    if (!form.name || !form.station || !form.classification || !form.type) {
      Swal.fire('Error', 'Please fill all the fields', 'error');
      return false;
    }
    if (!form.phoneNumber || form.phoneNumber.length !== 10) {
      Swal.fire('Error', 'Phone number should be 10 digits', 'error');
      return false;
    }
    // Check if phone number contains only digits
    if (!/^\d{10}$/.test(form.phoneNumber)) {
      Swal.fire('Error', 'Phone number should contain only digits', 'error');
      return false;
    }
    return true;
  };

  // Create / Update submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (isEditing) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/references/${editingId}`,
          form
        );
        Swal.fire('Success', 'Reference updated successfully', 'success');
      } else {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/references`, form);
        Swal.fire('Success', 'Reference added successfully', 'success');
      }
      fetchReferences();
      resetForm();
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error submitting reference:', error);
      const errorMessage = error.response?.data?.error || 'Failed to submit reference';
      Swal.fire('Error', errorMessage, 'error');
    }
  };

  // Reset form to default state
  const resetForm = () => {
    setForm({
      name: '',
      phoneNumber: '',
      classification: 'A',
      type: 'Interior',
      station: '',
      createdBy: user?.user?._id
    });
    setIsEditing(false);
    setEditingId(null);
  };

  // Input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle phone number input - only allow digits
    if (name === 'phoneNumber') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        const processedValue = numericValue;
        setForm({ ...form, [name]: processedValue });
      }
      return;
    }
    
    // Convert reference name to uppercase
    const processedValue = name === 'name' ? value.toUpperCase() : value;
    setForm({ ...form, [name]: processedValue });
  };

  // Station select
  const handleStationChange = (selectedOption) => {
    setForm({ ...form, station: selectedOption.value });
  };

  // Classification select
  const handleClassificationChange = (selectedOption) => {
    setForm({ ...form, classification: selectedOption.value });
  };

  // Type select
  const handleTypeChange = (selectedOption) => {
    setForm({ ...form, type: selectedOption.value });
  };

  // Pagination
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const totalPages = Math.ceil(totalReferences / limit);

  // Enhanced pagination helpers
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
      range.push(i);
    }

    if (page - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (page + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  // Helper function for Select styles
  const getSelectStyles = (isDark) => ({
    control: (base, state) => ({
      ...base,
      border: state.isFocused 
        ? `2px solid ${isDark ? '#9333EA' : '#9333EA'}` 
        : `1px solid ${isDark ? '#4B5563' : '#E5E7EB'}`,
      borderRadius: '12px',
      padding: '4px',
      backgroundColor: isDark ? '#374151' : 'white',
      boxShadow: state.isFocused ? `0 0 0 1px ${isDark ? '#9333EA' : '#9333EA'}` : 'none',
      '&:hover': {
        borderColor: isDark ? '#6B7280' : '#D1D5DB'
      }
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? '#374151' : 'white',
      borderRadius: '12px',
      border: `1px solid ${isDark ? '#4B5563' : '#E5E7EB'}`,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? '#9333EA' 
        : state.isFocused 
        ? (isDark ? '#4B5563' : '#F3E8FF')
        : (isDark ? '#374151' : 'white'),
      color: state.isSelected ? 'white' : (isDark ? '#F3F4F6' : '#374151'),
      '&:hover': {
        backgroundColor: state.isSelected ? '#9333EA' : (isDark ? '#4B5563' : '#F3E8FF')
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#374151'
    }),
    input: (base) => ({
      ...base,
      color: isDark ? '#F3F4F6' : '#374151'
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? '#9CA3AF' : '#6B7280'
    })
  });

  return (
    <div className={`min-h-screen p-3 sm:p-6 ${
      darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-purple-50'
    }`}>
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className={`rounded-2xl shadow-xl p-6 mb-8 border-l-4 border-purple-500 ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-6 lg:mb-0">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                Reference Management
              </h1>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Manage and organize your reference partners
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative group">
                <FiSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 transition-colors ${
                  darkMode ? 'text-gray-500 group-focus-within:text-purple-400' : 'text-gray-400 group-focus-within:text-purple-500'
                }`} />
                <input
                  type="text"
                  placeholder="Search references..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500 transition-all w-full sm:w-72 shadow-sm hover:shadow-md ${
                    darkMode 
                      ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-purple-500' 
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500 focus:border-purple-500'
                  }`}
                />
              </div>
              <button
                onClick={() => setIsFormOpen(true)}
                className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-6 py-3 rounded-xl flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
              >
                <FiPlus className="text-lg" /> Add New Reference
              </button>
            </div>
          </div>
        </div>

        {/* Form Modal */}
        {isFormOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className={`rounded-2xl shadow-2xl w-full max-w-md transform animate-in slide-in-from-bottom duration-300 ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className={`flex justify-between items-center p-6 border-b ${
                darkMode ? 'border-gray-700' : 'border-gray-100'
              }`}>
                <div>
                  <h2 className={`text-2xl font-bold ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                    {isEditing ? 'Edit Reference' : 'Add New Reference'}
                  </h2>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {isEditing ? 'Update reference information' : 'Enter reference details below'}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setIsFormOpen(false);
                    resetForm();
                  }}
                  className={`p-2 rounded-full transition-colors ${
                    darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <FiX size={24} />
                </button>
              </div>
              <div className="p-6 space-y-5">
                <div className="space-y-2">
                  <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Name *
                  </label>
                  <input
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleInputChange}
                    placeholder="Enter reference name (will be converted to uppercase)"
                    className={`w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-500 transition-all uppercase ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-purple-500' 
                        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500 hover:border-gray-300 focus:border-purple-500'
                    }`}
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <div className="space-y-2">
                  <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Phone Number *
                  </label>
                  <input
                    name="phoneNumber"
                    type="tel"
                    value={form.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="Enter 10-digit phone number"
                    maxLength="10"
                    pattern="[0-9]{10}"
                    className={`w-full border rounded-xl px-4 py-3 focus:ring-2 focus:ring-purple-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-400 focus:border-purple-500' 
                        : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500 hover:border-gray-300 focus:border-purple-500'
                    }`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Classification *
                    </label>
                    <Select
                      options={classificationOptions}
                      value={classificationOptions.find((opt) => opt.value === form.classification)}
                      onChange={handleClassificationChange}
                      placeholder="Select classification"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={getSelectStyles(darkMode)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Type *
                    </label>
                    <Select
                      options={typeOptions}
                      value={typeOptions.find((opt) => opt.value === form.type)}
                      onChange={handleTypeChange}
                      placeholder="Select type"
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={getSelectStyles(darkMode)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className={`block text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Station *
                  </label>
                  <Select
                    options={stations}
                    value={stations.find((opt) => opt.value === form.station)}
                    onChange={handleStationChange}
                    placeholder="Select station"
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={getSelectStyles(darkMode)}
                  />
                </div>
                <div className={`flex gap-3 pt-6 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                  <button
                    onClick={() => {
                      setIsFormOpen(false);
                      resetForm();
                    }}
                    className={`flex-1 px-4 py-3 rounded-xl transition-colors font-medium ${
                      darkMode 
                        ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-700' 
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white px-4 py-3 rounded-xl font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    {isEditing ? 'Update' : 'Add'} Reference
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* References Table */}
        <div className={`rounded-2xl shadow-xl overflow-hidden border ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
        }`}>
          <div className={`px-6 py-4 border-b ${
            darkMode ? 'bg-gray-750 border-gray-700' : 'bg-gradient-to-r from-gray-50 to-purple-50 border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold flex items-center gap-2 ${
              darkMode ? 'text-gray-100' : 'text-gray-800'
            }`}>
              <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
              References List
              <span className={`text-sm font-normal ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                ({totalReferences} total)
              </span>
            </h3>
          </div>

          {/* Desktop Table View - Hidden on Mobile */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={darkMode ? 'bg-gray-750' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-3 py-4 text-left text-xs font-bold uppercase tracking-wider w-16 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>ID</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Name</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Phone</th>
                  <th className={`px-3 py-4 text-left text-xs font-bold uppercase tracking-wider w-20 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Class</th>
                  <th className={`px-3 py-4 text-left text-xs font-bold uppercase tracking-wider w-24 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Type</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Station</th>
                  <th className={`px-4 py-4 text-left text-xs font-bold uppercase tracking-wider w-20 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${
                darkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-100'
              }`}>
                {references.map((reference, index) => (
                  <tr key={reference._id} className={`transition-colors group ${
                    darkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                  }`}>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span className={`text-xs font-mono px-2 py-1 rounded-md ${
                        darkMode ? 'text-gray-300 bg-gray-700' : 'text-gray-600 bg-gray-100'
                      }`}>
                        {reference.id}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                        {reference.name}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {reference.phoneNumber}
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${
                        reference.classification === 'A' ? 'bg-red-100 text-red-800 ring-1 ring-red-200' :
                        reference.classification === 'B' ? 'bg-orange-100 text-orange-800 ring-1 ring-orange-200' :
                        reference.classification === 'C' ? 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200' :
                        reference.classification === 'D' ? 'bg-green-100 text-green-800 ring-1 ring-green-200' :
                        'bg-blue-100 text-blue-800 ring-1 ring-blue-200'
                      }`}>
                        {reference.classification}
                      </span>
                    </td>
                    <td className="px-3 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium truncate max-w-20 ${
                        darkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {reference.type}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium truncate max-w-24 ${
                        darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {reference.station?.name || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={() => {
                          setIsEditing(true);
                          setEditingId(reference._id);
                          setForm({
                            name: reference.name,
                            phoneNumber: reference.phoneNumber,
                            classification: reference.classification,
                            type: reference.type,
                            station: reference.station?._id,
                            createdBy: user?.user?._id
                          });
                          setIsFormOpen(true);
                        }}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all group-hover:shadow-md ${
                          darkMode 
                            ? 'text-purple-400 hover:text-purple-300 hover:bg-purple-900' 
                            : 'text-purple-600 hover:text-purple-800 hover:bg-purple-50'
                        }`}
                      >
                        <FiEdit2 size={14} />
                        <span>Edit</span>
                      </button>
                    </td>
                  </tr>
                ))}
                {references.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                          darkMode ? 'bg-gray-700' : 'bg-gray-100'
                        }`}>
                          <FiSearch className={`text-2xl ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        </div>
                        <p className={`text-lg font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          No references found
                        </p>
                        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          Try adjusting your search terms
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View - Hidden on Desktop */}
          <div className="md:hidden">
            {references.length > 0 ? (
              <div className="divide-y divide-gray-200">
                {references.map((reference, index) => (
                  <div 
                    key={reference._id} 
                    className={`p-4 transition-colors ${
                      darkMode ? 'hover:bg-gray-750' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                            darkMode ? 'text-gray-300 bg-gray-700' : 'text-gray-600 bg-gray-100'
                          }`}>
                            {reference.id}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold uppercase ${
                            reference.classification === 'A' ? 'bg-red-100 text-red-800' :
                            reference.classification === 'B' ? 'bg-orange-100 text-orange-800' :
                            reference.classification === 'C' ? 'bg-yellow-100 text-yellow-800' :
                            reference.classification === 'D' ? 'bg-green-100 text-green-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {reference.classification}
                          </span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            darkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800'
                          }`}>
                            {reference.type}
                          </span>
                        </div>
                        <h3 className={`text-base font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                          {reference.name}
                        </h3>
                      </div>
                      <button
                        onClick={() => {
                          setIsEditing(true);
                          setEditingId(reference._id);
                          setForm({
                            name: reference.name,
                            phoneNumber: reference.phoneNumber,
                            classification: reference.classification,
                            type: reference.type,
                            station: reference.station?._id,
                            createdBy: user?.user?._id
                          });
                          setIsFormOpen(true);
                        }}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all shadow-sm ${
                          darkMode 
                            ? 'text-purple-400 bg-purple-900 hover:bg-purple-800' 
                            : 'text-purple-600 bg-purple-50 hover:bg-purple-100'
                        }`}
                      >
                        <FiEdit2 size={14} />
                        <span className="text-sm font-medium">Edit</span>
                      </button>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <svg className={`w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {reference.phoneNumber}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <svg className={`w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {reference.station?.name || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-12 text-center">
                <div className="flex flex-col items-center justify-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                    darkMode ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                    <FiSearch className={`text-2xl ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                  </div>
                  <p className={`text-lg font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    No references found
                  </p>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Try adjusting your search terms
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`rounded-2xl shadow-lg border mt-6 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
          }`}>
            <div className={`px-4 sm:px-6 py-3 sm:py-4 border-t ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                  <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="hidden sm:inline">Showing </span>
                    <span className="font-medium">{((page - 1) * limit) + 1}</span>
                    <span className="hidden sm:inline"> to </span>
                    <span className="sm:hidden">-</span>
                    <span className="font-medium">{Math.min(page * limit, totalReferences)}</span>
                    <span className="hidden sm:inline"> of </span>
                    <span className="sm:hidden">/</span>
                    <span className="font-medium">{totalReferences}</span>
                    <span className="hidden sm:inline"> results</span>
                  </p>
                  <select
                    value={limit}
                    onChange={(e) => {
                      setLimit(parseInt(e.target.value));
                      setPage(1);
                    }}
                    className={`px-2 sm:px-3 py-1 border rounded-lg text-xs sm:text-sm focus:ring-2 focus:ring-purple-500 transition-all ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-200' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
                
                <nav className="flex items-center justify-center sm:justify-start space-x-1">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className={`relative inline-flex items-center px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium border rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      darkMode 
                        ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                        : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="hidden sm:inline">Previous</span>
                    <span className="sm:hidden">Prev</span>
                  </button>
                  
                  <div className="hidden sm:flex items-center space-x-1">
                    {getPageNumbers().map((pageNum, index) => (
                      pageNum === '...' ? (
                        <span key={index} className={`px-3 py-1 text-sm ${
                          darkMode ? 'text-gray-500' : 'text-gray-500'
                        }`}>...</span>
                      ) : (
                        <button
                          key={index}
                          onClick={() => handlePageChange(pageNum)}
                          className={`relative inline-flex items-center px-3 py-1 text-sm font-medium border rounded-md transition-all ${
                            pageNum === page
                              ? darkMode 
                                ? 'z-10 bg-purple-500/20 border-purple-500 text-purple-400' 
                                : 'z-10 bg-purple-50 border-purple-500 text-purple-600'
                              : darkMode 
                                ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600' 
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      )
                    ))}
                  </div>
                  
                  {/* Mobile: Show only current page */}
                  <div className="sm:hidden flex items-center">
                    <span className={`px-2 py-1 text-xs font-medium ${
                      darkMode ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {page} / {totalPages}
                    </span>
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className={`relative inline-flex items-center px-2 sm:px-3 py-1 text-xs sm:text-sm font-medium border rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      darkMode 
                        ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                        : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}